import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { BRAND } from "@/lib/brand";
import { BrandLogo } from "@/components/BrandLogo";
import Reveal from "@/components/Reveal";
import RippleAccent from "@/components/media/RippleAccent";

const principles = [
  { n: "I", title: "Say less, and mean it", text: "When a detail is not yet confirmed, we write \u201cto be confirmed\u201d and leave the gap. We never invent claims, ingredients or certifications." },
  { n: "II", title: "Listen before we speak", text: "Members\u2019 experiences\u2014positive and critical\u2014are read, considered and, where appropriate, allowed to inform our work. We publish what we heard and what we did, each with a date." },
  { n: "III", title: "Experiences, not evidence", text: "What the community shares is real and valued, but it is not clinical proof and we will never dress it up as such." },
];

export default function About() {
  return (
    <div data-testid="founder-story">
      {/* OPENING */}
      <section className="relative overflow-hidden">
        <BrandLogo variant="mark" className="pointer-events-none select-none absolute -right-28 -top-10 w-[560px] opacity-[0.05]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-16 sm:pt-24 sm:pb-24 grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8 animate-fade-up">
            <p className="eyebrow">A note from the founder</p>
            <h1 className="mt-8 font-serif font-light text-5xl sm:text-6xl lg:text-7xl tracking-tight text-forest leading-[0.95] text-balance" data-testid="founder-title">
              Skincare was never meant to be a monologue.
            </h1>
          </div>
          <p className="lg:col-span-4 text-base sm:text-lg leading-relaxed text-charcoal/65 font-light animate-fade-up" style={{ animationDelay: "120ms" }}>
            ELA Rituals makes considered skincare. {BRAND.community} is the community we built so the conversation could run both ways.
          </p>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"><div className="hairline" /></div>
      </section>

      {/* THE LETTER */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28 grid lg:grid-cols-12 gap-12">
        <Reveal className="lg:col-span-4">
          <div className="lg:sticky lg:top-40">
            <BrandLogo variant="mark" className="h-24 w-auto" />
            <p className="mt-8 font-serif italic text-2xl text-champagne">{BRAND.tagline}</p>
            <p className="mt-4 text-sm text-charcoal/55 font-light leading-relaxed max-w-xs">
              Written for the people who will use our products, and for the ones who will tell us the truth about them.
            </p>
          </div>
        </Reveal>
        <div className="lg:col-span-7 lg:col-start-6">
          <Reveal>
            <p className="drop-cap text-lg sm:text-xl leading-[1.75] text-charcoal/80 font-light mb-7" data-testid="founder-letter-first">
              I started ELA Rituals with a small, stubborn idea: that skincare should be made with intention and talked about with honesty. Not every brand needs a community. But I kept coming back to the same question—if we are asking people to make our products part of their everyday ritual, shouldn't they have a way to tell us how that ritual actually goes?
            </p>
            <p className="text-lg sm:text-xl leading-[1.75] text-charcoal/80 font-light mb-7">
              So we built {BRAND.community}. It is free to join, and I want to be clear from the first line that joining does not guarantee anyone a product. Programmes open when they open, with their own eligibility, and we would rather say so plainly than leave it to the small print.
            </p>
          </Reveal>
          <Reveal>
            <figure className="pull-quote my-14">
              <blockquote className="font-serif font-light italic text-3xl sm:text-4xl leading-[1.2] text-forest text-balance">
                Good skincare is a shared journey. We listen to real experiences, and we let honest feedback inform our work.
              </blockquote>
              <figcaption className="mt-5 text-[11px] uppercase tracking-[0.24em] text-champagne">— Founder, ELA Rituals</figcaption>
            </figure>
          </Reveal>
          <Reveal>
            <h2 className="font-serif font-light text-3xl sm:text-4xl text-forest mt-4 mb-6">Three products, and nothing we cannot stand behind</h2>
            <p className="text-lg sm:text-xl leading-[1.75] text-charcoal/80 font-light mb-7">
              We launch with three: Serum, Sunscreen and Facewash. Each has its own page, and each has details still marked "to be confirmed". That is deliberate. Formulation information will be shared as it is approved, and until then I would rather show you a gap than fill it with something we cannot keep.
            </p>
            <p className="text-lg sm:text-xl leading-[1.75] text-charcoal/80 font-light mb-7">
              The same goes for the community. When members share their experiences, we treat them as exactly that—experiences, not evidence. We will tell you what we heard and what we decided, with dates, and we will not pretend every suggestion is adopted.
            </p>
          </Reveal>
          <Reveal>
            <h2 className="font-serif font-light text-3xl sm:text-4xl text-forest mt-16 mb-6">Discover. Experience. Share.</h2>
            <p className="text-lg sm:text-xl leading-[1.75] text-charcoal/80 font-light mb-7">
              Those three words are the whole architecture of {BRAND.community}. Discover what a product is for. Experience it in your own light, over weeks rather than minutes. Share what you found—privately with us, or publicly if you choose. Every part of that is voluntary, and none of it is a test.
            </p>
            <p className="text-lg sm:text-xl leading-[1.75] text-charcoal/80 font-light">
              Thank you for being here at the beginning. One ritual at a time.
            </p>
            <div className="mt-12 flex items-center gap-5">
              <BrandLogo variant="mark" className="h-10 w-auto" />
              <div>
                <p className="font-serif italic text-2xl text-forest">— ELA</p>
                <p className="text-[11px] uppercase tracking-[0.22em] text-charcoal/45 mt-1">Founder, ELA Rituals</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* PRINCIPLES */}
      <section className="relative bg-forest text-ivory overflow-hidden grain" data-testid="founder-principles">
        <RippleAccent className="absolute -left-56 -bottom-64 w-[760px] h-[760px] opacity-50" rings={5} testid="about-ripple-accent" />
        <BrandLogo variant="mark" tone="light" className="pointer-events-none select-none absolute -right-20 -top-24 w-[520px] opacity-[0.05]" />
        <Reveal className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32">
          <p className="eyebrow text-champagne">How we hold ourselves</p>
          <h2 className="mt-6 font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[0.98] text-balance max-w-3xl">Three promises, written down so you can hold us to them</h2>
          <ol className="mt-16 grid md:grid-cols-3 gap-px bg-ivory/15 border border-ivory/15">
            {principles.map((p) => (
              <li key={p.n} className="bg-forest/40 backdrop-blur-sm p-8 lg:p-10 min-h-[260px] flex flex-col">
                <span className="numeral">{p.n}</span>
                <h3 className="mt-6 font-serif text-3xl font-light leading-tight">{p.title}</h3>
                <p className="mt-4 text-sm text-ivory/65 font-light leading-relaxed">{p.text}</p>
              </li>
            ))}
          </ol>
        </Reveal>
      </section>

      {/* INVITATION */}
      <Reveal as="section" className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32 text-center">
        <p className="eyebrow eyebrow-plain justify-center">{BRAND.participation}</p>
        <h2 className="mt-8 font-serif font-light text-4xl sm:text-5xl text-forest text-balance leading-[0.98]">Come and see how the ritual unfolds</h2>
        <p className="mt-6 text-charcoal/65 font-light max-w-xl mx-auto leading-relaxed">Read the journal, explore the three products, or join the community and tell us what you find.</p>
        <div className="mt-10 flex flex-col sm:flex-row justify-center gap-4">
          <Link to="/join" className="btn btn-primary" data-testid="about-join-cta">{BRAND.joinCta} <ArrowRight size={14} /></Link>
          <Link to="/journal" className="btn btn-outline" data-testid="about-journal-cta">Read the Journal</Link>
        </div>
      </Reveal>
    </div>
  );
}
